<template>
  <div
    id="container"
    @click="handleClick"
  >
    <div
      v-if="state === 'loading'"
      id="spinner-container"
    >
      <LoadIcon
        id="spinner"
        :size="26"
      />
    </div>
    <div
      v-else
      id="icon-container"
      :class="`${state}`"
    >
      <i
        id="icon"
        :class="`fa-solid fa-location-crosshairs`"
      />
    </div>
  </div>
</template>

<script>
// @ts-check
import {gmAPI, MapService} from '@/services/MapService';
import LoadIcon from '@/components/svgs/LoadIcon.vue';
import {mapActions} from 'vuex';

/** @type {number} */
const TIMEOUT = 5000;
/**
 * @type {Object} See https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/getCurrentPosition
 */
const GEOLOCATION_OPTIONS = {
  maximumAge: 0,
  timeout: TIMEOUT,
  enableHighAccuracy: true,
};

/**
 * Wraps `geolocation.getCurrentPosition` in Promise API and adds default options argument.
 * @return {Promise<GeolocationPosition, GeolocationPositionError>} Promise resolving to device's position.
 */
const getPosition = () => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, GEOLOCATION_OPTIONS);
  });
};

/**
 * @typedef {'disabled'|'loading'|'enabled'|'unavailable'} GPSState
 * disabled: GPS feature currently inactive and can be activated by user.
 * loading: currently retrieving user's location for first time & creating map marker.
 * enabled: GPS feature enabled and is polling user's location.
 * unavailable: we do not have access / permissions to geolocation services.
 */

export default {
  components: {
    LoadIcon,
  },
  data() {
    /** @type {google.maps.MarkerOptions} */
    const opts = {
      map: null,
      position: {lat: 0, lng: 0},
      title: 'You are here',
      clickable: false,
      draggable: false,
      icon: {
        fillColor: 'rgb(100, 145, 220)',
        fillOpacity: 1,
        size: new gmAPI.Size(10, 10),
        strokeColor: 'white',
        strokeWeight: 2,
        path: gmAPI.SymbolPath.CIRCLE,
        scale: 9,
      },
    };
    return {
      /** @type {GPSState} */
      state: 'disabled',
      /** @type {NodeJS.Timeout} The interval ID, for use with clearInterval */
      updateInterval: undefined,
      markerCoords: undefined,
      marker: new gmAPI.Marker(opts),
    };
  },
  async mounted() {
    const geoPerm = (await this.getGeolocationPermissions());
    if (geoPerm === 'denied') {
      this.state = 'unavailable';
    }
  },
  methods: {
    ...mapActions(['addErrorMessage']),
    /**
     * Uses the permissions API to determine if we have permission to use geolocation.
     * @return {Promise<'granted'|'denied'|'prompt'>} A string describing our geolocation API access.
     */
    async getGeolocationPermissions() {
      // Assume permission is denied if we don't have access to the permissions API.
      const geoPerm = (await navigator?.permissions?.query({name: 'geolocation'}));
      return geoPerm?.state ?? 'denied';
    },
    /**
     * Click handler for the user clicking the GPS button.
     */
    async handleClick() {
      if (this.state === 'disabled' || this.state === 'unavailable') {
        this.enableGPS();
      } else if (this.state === 'enabled' && this.markerCoords) {
        MapService.centerViewport({lat: this.markerCoords.latitude, lng: this.markerCoords.longitude}, 17);
      }
    },
    /**
     * Enable GPS tracking for the user.
     * Obtains the user's current position, moves the map to that location, and places a map marker.
     * Registers an interval to periodically obtain the user's new position and move the map marker.
     */
    async enableGPS() {
      this.state = 'loading';
      this.marker.setMap(MapService.getMap());
      this.updateInterval = setInterval(this.updatePosition, TIMEOUT);
      // This needs to go after starting the interval. If it fails, it would prevent the interval from starting and
      // retrying when timeouts occur. Only reposition the viewport when we first enable GPS, not on subsequent updates.
      this.updatePosition(true);
    },
    async updatePosition(repositionViewport = false) {
      try {
        const {coords} = await getPosition();
        this.moveMarker(coords);
        if (repositionViewport) {
          MapService.centerViewport({lat: coords.latitude, lng: coords.longitude}, 17);
        }
        this.state = 'enabled';
      } catch (err) {
        this.handleGeolocationError(err);
      }
    },
    /**
     * Disables GPS tracking for the user.
     * Removes the marker from the map and removes the position update interval.
     */
    async disableGPS() {
      this.state = 'disabled';
      clearInterval(this.updateInterval);
      this.marker.setMap(null);
    },
    /**
     * Handles errors returned from the geolocation API.
     * @param {GeolocationPositionError} error An error returned from `getCurrentPosition`.
     */
    handleGeolocationError(error) {
      const fatalError = (message) => {
        this.addErrorMessage(`Unable to update your location: ${message}`);
        console.error(error);
      };
      switch (error.code) {
        case 1: // PERMISSION_DENIED
          fatalError(`Beyond Maps doesn't have permission to see your location. Please check your browser and device settings.`);
          this.disableGPS().then(() => this.state = 'unavailable');
          break;
        case 2: // POSITION_UNAVAILABLE
          fatalError('Beyond Maps was unable to track your position due to a device error.');
          this.disableGPS();
          break;
        case 3: // TIMEOUT
          // Optimistically assume we will succeed next time.
          console.warn('Geolocation timeout. Will try again.', error);
          break;
        default:
          // Some other unknown error.
          fatalError('An unknown error occurred while trying to track your location.');
          this.disableGPS();
          break;
      }
    },
    /**
     * Relocates (or creates) the GPS map marker.
     * @param {GeolocationCoordinates} coords A coordinates object returned from `getCurrentPosition`.
     */
    moveMarker({latitude, longitude}) {
      this.markerCoords = {latitude, longitude};
      this.marker.setPosition({lat: latitude, lng: longitude});
    },
  },
};
</script>

<style scoped lang="scss">
    #container {
        position: fixed;
        right: 20px;
        bottom: 30px;
        width: 40px;
        height: 40px;
        box-shadow: rgb(0 0 0 / 30%) 0px 1px 4px -1px;
        border-radius: 2px;
        cursor: pointer;
        background-color: white;
        z-index: 1;
    }
    #spinner-container {
      padding: 7px;
    }
    #icon-container {
      padding: 7px;
    }
    #icon {
        width: 26px;
        height: 26px;
    }
    .disabled {
            color: rgb(102, 102, 102);
        }
    .enabled {
            color: rgb(100, 145, 220);
        }
    .unavailable {
            color: rgb(230, 230, 230);
    }
</style>
